import {ApiError} from "./api-error";
import {notifyUnauthorized} from "./auth-events";
import {csrfTokenManager} from "./csrf-token-manager";

const UNSAFE_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

export async function customFetch<T>(url: string, options: RequestInit = {}): Promise<T> {
  const method = (options.method ?? "GET").toUpperCase();
  const headers = new Headers(options.headers);
  const csrfToken = csrfTokenManager.getToken();

  if (UNSAFE_METHODS.has(method) && csrfToken && !headers.has("X-CSRF-Token")) {
    headers.set("X-CSRF-Token", csrfToken);
  }

  const response = await fetch(url, {
    ...options,
    credentials: options.credentials ?? "same-origin",
    headers,
  });

  if (!response.ok) {
    if (response.status === 401) {
      csrfTokenManager.clearToken();
      notifyUnauthorized();
    }

    throw await ApiError.fromResponse(response);
  }

  return parseResponse<T>(response);
}

async function parseResponse<T>(response: Response): Promise<T> {
  if ([204, 205, 304].includes(response.status) || !response.body) {
    return undefined as T;
  }

  const contentType = response.headers.get("content-type") ?? "";

  if (contentType.includes("application/json")) {
    return response.json() as Promise<T>;
  }

  if (contentType.includes("text/")) {
    return response.text() as Promise<T>;
  }

  if (contentType.includes("application/octet-stream")) {
    return response.blob() as Promise<T>;
  }

  return response.text() as Promise<T>;
}
